import { CharacterCount } from "@knowt/editor/extensions/characterCount";
import { CodeBlockLowlight } from "@knowt/editor/extensions/codeBlockLowlight";
import { Color } from "@knowt/editor/extensions/color";
import { Details } from "@knowt/editor/extensions/details";
import { DetailsContent } from "@knowt/editor/extensions/detailsContent";
import { DetailsSummary } from "@knowt/editor/extensions/detailsSummary";
import { Document } from "@knowt/editor/extensions/document";
import { Dropcursor } from "@knowt/editor/extensions/dropcursor";
import { Emoji } from "@knowt/editor/extensions/emoji";
import { Figcaption } from "@knowt/editor/extensions/figcaption";
import { FileHandler } from "@knowt/editor/extensions/fileHandler";
import Focus from "@knowt/editor/extensions/focus";
import { FontFamily } from "@knowt/editor/extensions/fontFamily";
import { FontSize } from "@knowt/editor/extensions/fontSize";
import { Highlight } from "@knowt/editor/extensions/highlight";
import { HorizontalRule } from "@knowt/editor/extensions/horizontalRule";
import { Link } from "@knowt/editor/extensions/link";
import { Mathematics, MathematicsOptions } from "@knowt/editor/extensions/mathematics";
import { Placeholder } from "@knowt/editor/extensions/placeholder";
import { Selection } from "@knowt/editor/extensions/selection";
import { StarterKit } from "@knowt/editor/extensions/starterkit";
import { Subscript } from "@knowt/editor/extensions/subscript";
import { Superscript } from "@knowt/editor/extensions/superscript";
import { TableOfContents } from "@knowt/editor/extensions/tableOfContents";
import { TaskItem } from "@knowt/editor/extensions/taskItem";
import { TaskList } from "@knowt/editor/extensions/taskList";
import { TextAlign } from "@knowt/editor/extensions/textAlign";
import { TextStyle } from "@knowt/editor/extensions/textStyle";
import { Typography } from "@knowt/editor/extensions/typography";
import { Underline } from "@knowt/editor/extensions/underline";
import { Youtube } from "@knowt/editor/extensions/youtube";
import { Editor, ReactNodeViewRenderer } from "@knowt/editor/react";
import React from "react";
import { BlockquoteFigure } from "../extensions/BlockquoteFigure";
import CodeBlockComponent from "../nodeViews/CodeBlockComponent";
import { uploadImage } from "../utils/uploadImage";
import { AiInputMenuTrigger } from "./AiInputMenuTrigger";
import { CustomPlaceholder } from "./CustomPlaceholder";
import { DragHandleListener } from "./DragHandleListener";
import { emojiSuggestion } from "./EmojiSuggestion/emojiSuggestion";
import { GhostText, GhostTextOptions } from "./GhostText";
import { Heading } from "./Heading";
import { ImageBlock } from "./ImageBlock";
import { ImageUpload } from "./ImageUpload";
import { Column, Columns } from "./MultiColumn";
import { NonPersistentHighlight } from "./NonPersistentHighlight";
import { SlashCommand } from "./SlashCommand";
import { Table, TableCell, TableHeader, TableRow } from "./Table";
import { TrailingNode } from "./TrailingNode";

export const isSlashCommandAllowed = ({ editor }: { editor: Editor }) => {
    return !editor.isActive("codeBlock") && !editor.isActive("image");
};

export const extensionKit = ({
    placeholderOptions,
    customPlaceholder,
    characterCountLimit,
    onAITrigger,
    copilotProps,
    onKatexClick,
}: {
    editable?: boolean;
    placeholderOptions?: { placeholder: string };
    customPlaceholder?: ({ editor }: { editor: Editor }) => React.ReactElement<any>;
    characterCountLimit?: number;
    onAITrigger?: ({ editor }: { editor: Editor }) => void;
    copilotProps?: {
        fetchSuggestion?: GhostTextOptions["fetchSuggestion"];
        fetchAlternateSuggestion?: GhostTextOptions["fetchAlternateSuggestion"];
        onSuggestionAccept?: GhostTextOptions["onSuggestionAccept"];
        disabled?: GhostTextOptions["disabled"];
        timeout?: number;
    };
    onKatexClick?: MathematicsOptions["onClick"];
}) => [
    Document,
    Columns,
    TaskList,
    TaskItem.configure({ nested: true }),

    Column,
    Selection,

    Heading.configure({ levels: [1, 2, 3, 4, 5] }),

    HorizontalRule,
    StarterKit.configure({
        document: false,
        dropcursor: false,
        heading: false,
        horizontalRule: false,
        blockquote: false,
        codeBlock: false,
    }),
    CodeBlockLowlight.extend({
        addNodeView() {
            return ReactNodeViewRenderer(CodeBlockComponent);
        },
    }),
    TextStyle,
    FontSize,
    FontFamily,
    Color,

    TrailingNode,

    Link.configure({ openOnClick: false }),
    Highlight.configure({ multicolor: true }),
    Underline,
    CharacterCount.configure({
        limit: typeof characterCountLimit === "number" ? characterCountLimit : Number.POSITIVE_INFINITY,
    }),
    TableOfContents,

    ImageUpload,
    ImageBlock,

    FileHandler.configure({
        allowedMimeTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
        onDrop: async (currentEditor, files, pos) => {
            for (const file of files) {
                if (file.type.includes("image")) {
                    const url = await uploadImage(file);
                    currentEditor.chain().setImageBlockAt({ pos, src: url }).focus().run();
                }
            }
        },
        onPaste: async (currentEditor, files) => {
            for (const file of files) {
                if (file.type.includes("image")) {
                    const url = await uploadImage(file);
                    return currentEditor
                        .chain()
                        .setImageBlockAt({ pos: currentEditor.state.selection.anchor, src: url })
                        .focus()
                        .run();
                }
            }
        },
    }),

    Emoji.configure({ enableEmoticons: true, suggestion: emojiSuggestion }),

    TextAlign.extend({
        addKeyboardShortcuts() {
            return {};
        },
    }).configure({ types: ["heading", "paragraph"] }),
    Subscript,
    Superscript,

    Table,
    TableCell,
    TableHeader,
    TableRow,

    Typography,

    ...(typeof customPlaceholder === "function"
        ? [CustomPlaceholder.configure({ placeholder: customPlaceholder })]
        : []),

    Placeholder.configure({
        includeChildren: true,
        showOnlyCurrent: true,
        placeholder: ({ editor }) => {
            if (editor.isActive("heading")) {
                return `Heading ${editor.getAttributes("heading").level}`;
            }
            return placeholderOptions?.placeholder ?? "Press 'space' for Al and '/' for formatting";
        },
    }),

    SlashCommand.configure(),

    Focus,
    Figcaption,

    BlockquoteFigure,
    Dropcursor.configure({
        width: 2,
        class: "ProseMirror-dropcursor border-black",
    }),

    Mathematics.configure({ onClick: onKatexClick }),
    Details.configure({ HTMLAttributes: { class: "details" } }),
    DetailsSummary,
    DetailsContent,
    Youtube,
    NonPersistentHighlight,
    DragHandleListener,

    ...(typeof onAITrigger !== "function"
        ? []
        : [
              AiInputMenuTrigger.configure({
                  canShow: ({ editor }) => isSlashCommandAllowed({ editor }),
                  onAITrigger,
              }),
          ]),

    ...(copilotProps ? [GhostText.configure(copilotProps)] : []),
];
